import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ChatIcon from "@material-ui/icons/Chat";
import { useDialogStyles } from "../../theme/redesign";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../../components/ConfirmationModal";
import NewTicketModal from "../../components/NewTicketModal";
import {
  RecordCard,
  RecordCardMeta,
  RecordsCardsGrid,
  useViewMode,
  ViewModeToggle,
} from "../../components/ViewModeToggle";
import { formatDate, looksLikePhone, matchesQuery, storedPhone } from "./helpers";
import { ContactAvatar, EmptyHint, LoadingHint, NegocioPager, PageFrame, PhoneFields, PhoneWithFlag, SearchField, stopRow, tNegocio, useMobile, usePager } from "./shared";
import { AccountLiveStatus, AccountMark, AccountPeek } from "./AccountPeek";
import { useRecords } from "./Lists";
import { useStyles } from "./styles";

const emptyForm = () => ({ name: "", phone: "", telegram: "", notes: "" });

export default function SuppliersPage() {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const isMobile = useMobile();
  const history = useHistory();
  const { records, loading, reload } = useRecords("suppliers");
  const [viewMode, setViewMode] = useViewMode("negocio-suppliers");
  const [query, setQuery] = useState("");
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [peekAccountId, setPeekAccountId] = useState(null);
  const [ticketContact, setTicketContact] = useState(null);

  const openTicket = (row) => {
    const hasWa = looksLikePhone(row?.phone);
    const hasTg = Boolean(String(row?.telegram || "").replace(/^@+/, "").trim());
    if (!hasWa && !hasTg) {
      toast.error(i18n.t("newTicketModal.errors.noChannel"));
      return;
    }
    setTicketContact({
      id: row.id,
      conversationSource: "supplier",
      conversationId: row.id,
      name: row.name,
      number: row.phone,
      telegram: row.telegram,
    });
  };

  const rows = useMemo(
    () => records.filter((s) => matchesQuery(s, query, [s.name, s.phone, s.telegram, s.notes])),
    [records, query]
  );
  const pager = usePager(rows);

  const save = async () => {
    if (!form) return;
    try {
      const payload = {
        name: form.name,
        phone: storedPhone(form.phone),
        telegram: String(form.telegram || "").replace(/^@/, ""),
        notes: form.notes,
      };
      if (form.id) await api.put(`/negocio/suppliers/${form.id}`, payload);
      else await api.post("/negocio/suppliers", payload);
      toast.success(tNegocio("toastSaved"));
      setForm(null);
      reload();
    } catch (e) {
      toastError(e);
    }
  };

  const remove = async () => {
    if (!confirm) return;
    try {
      await api.delete(`/negocio/suppliers/${confirm.id}`);
      toast.success(tNegocio("toastSaved"));
      setConfirm(null);
      setDetail(null);
      reload();
    } catch (e) {
      toastError(e);
    }
  };

  return (
    <>
      <PageFrame
        title={tNegocio("pages.suppliers.title")}
        subtitle={tNegocio("pages.suppliers.subtitle")}
        actions={
          <>
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
            <Button variant="contained" color="primary" onClick={() => setForm(emptyForm())}>
              {tNegocio("new.supplier")}
            </Button>
          </>
        }
      >
        <div className={classes.toolbar}>
          <span />
          <SearchField value={query} onChange={setQuery} placeholder={tNegocio("pages.suppliers.search")} />
        </div>
        {rows.length && viewMode === "cards" ? (
          <>
          <RecordsCardsGrid>
            {pager.slice.map((s) => (
                <RecordCard
                  key={s.id}
                  onClick={() => setDetail(s)}
                  actions={
                    <div className={classes.actionCluster}>
                      <IconButton size="small" onClick={stopRow(() => openTicket(s))}>
                        <ChatIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={stopRow(() => setForm({ ...emptyForm(), ...s }))}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={stopRow(() => setConfirm(s))}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </div>
                  }
                >
                  <div className={classes.agendaCell}>
                    <ContactAvatar name={s.name} number={s.phone} profilePicUrl={s.profilePicUrl} size={48} />
                    <div className={classes.agendaBody}>
                      <div style={{ fontWeight: 800 }}>{s.name}</div>
                    </div>
                  </div>
                  <RecordCardMeta>
                    <PhoneWithFlag number={s.phone} />
                    {s.telegram ? <div>@{s.telegram}</div> : null}
                  </RecordCardMeta>
                  <div className={classes.captionBlock}>
                    {(s.accounts || []).length} {tNegocio("cols.accounts")}
                  </div>
                </RecordCard>
              ))}
          </RecordsCardsGrid>
          <NegocioPager
            count={pager.count}
            page={pager.page}
            rowsPerPage={pager.rowsPerPage}
            onChangePage={pager.setPage}
            onChangeRowsPerPage={pager.setRowsPerPage}
          />
          </>
        ) : rows.length ? (
          <div className={classes.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tNegocio("cols.name")}</TableCell>
                  <TableCell>{tNegocio("cols.phone")}</TableCell>
                  <TableCell>{tNegocio("cols.telegram")}</TableCell>
                  <TableCell>{tNegocio("cols.accounts")}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {pager.slice.map((s) => (
                  <TableRow key={s.id} hover className={classes.clickableRow} onClick={() => setDetail(s)}>
                      <TableCell>
                        <div className={classes.agendaCell}>
                          <ContactAvatar name={s.name} number={s.phone} profilePicUrl={s.profilePicUrl} />
                          <div className={classes.agendaBody}>
                            <div style={{ fontWeight: 700 }}>{s.name}</div>
                            {s.notes ? <div className={classes.captionBlock}>{s.notes}</div> : null}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <PhoneWithFlag number={s.phone} />
                      </TableCell>
                      <TableCell>{s.telegram ? `@${s.telegram}` : "—"}</TableCell>
                      <TableCell>{(s.accounts || []).length}</TableCell>
                      <TableCell align="right" className={classes.stickyActions} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.actionCluster}>
                          <IconButton size="small" onClick={() => openTicket(s)}>
                            <ChatIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => setForm({ ...emptyForm(), ...s })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => setConfirm(s)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
            <NegocioPager
              count={pager.count}
              page={pager.page}
              rowsPerPage={pager.rowsPerPage}
              onChangePage={pager.setPage}
              onChangeRowsPerPage={pager.setRowsPerPage}
            />
          </div>
        ) : loading ? (
          <LoadingHint />
        ) : (
          <EmptyHint
            title={tNegocio("pages.suppliers.emptyTitle")}
            subtitle={tNegocio("pages.suppliers.empty")}
          />
        )}
      </PageFrame>

      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="sm" fullWidth classes={{ paper: dlg.paper }}>
        <DialogTitle>{form?.id ? tNegocio("edit") : tNegocio("new.supplier")}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.name")}
            value={form?.name || ""}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <PhoneFields
            value={form?.phone || ""}
            onChange={(phone) => setForm((f) => ({ ...f, phone }))}
          />
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.telegram")}
            value={form?.telegram || ""}
            onChange={(e) => setForm((f) => ({ ...f, telegram: e.target.value }))}
            helperText={tNegocio("pages.customers.telegramHint")}
          />
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            multiline
            rows={3}
            label={tNegocio("fields.notes")}
            value={form?.notes || ""}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForm(null)}>{tNegocio("close")}</Button>
          <Button color="primary" variant="contained" onClick={save}>
            {tNegocio("save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth classes={{ paper: dlg.paper }}>
        <DialogTitle>
          <div className={classes.agendaCell}>
            <ContactAvatar name={detail?.name} number={detail?.phone} profilePicUrl={detail?.profilePicUrl} size={48} />
            <div>
              {detail?.name}
              <div className={classes.captionBlock}>
                <PhoneWithFlag number={detail?.phone} />
                {detail?.telegram ? ` · @${detail.telegram}` : ""}
              </div>
            </div>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <div className={classes.sectionTitle}>{tNegocio("pages.suppliers.linkedAccounts")}</div>
          {(detail?.accounts || []).length ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tNegocio("cols.product")}</TableCell>
                  <TableCell>{tNegocio("cols.email")}</TableCell>
                  <TableCell>{tNegocio("cols.endDate")}</TableCell>
                  <TableCell>{tNegocio("cols.status")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(detail.accounts || []).map((a) => (
                  <TableRow
                    key={a.id}
                    hover
                    className={classes.clickableRow}
                    onClick={() => setPeekAccountId(a.id)}
                  >
                    <TableCell>{a.product?.name || "—"}</TableCell>
                    <TableCell>
                      <AccountMark account={a} product={a.product} email={a.email} />
                    </TableCell>
                    <TableCell>{formatDate(a.endDate)}</TableCell>
                    <TableCell>
                      <AccountLiveStatus account={a} compact />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <EmptyHint
              title={tNegocio("pages.suppliers.noAccounts")}
              subtitle={tNegocio("pages.suppliers.noAccountsHint")}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>{tNegocio("close")}</Button>
          {isMobile ? (
            <Tooltip title={tNegocio("pages.customers.newTicket")}>
              <IconButton onClick={() => openTicket(detail)}>
                <ChatIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Button startIcon={<ChatIcon />} onClick={() => openTicket(detail)}>
              {tNegocio("pages.customers.newTicket")}
            </Button>
          )}
          <Button
            onClick={() => {
              setForm({ ...emptyForm(), ...detail });
              setDetail(null);
            }}
          >
            {tNegocio("edit")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => history.push(`/negocio/cuentas?supplierId=${detail?.id || ""}`)}
          >
            {tNegocio("related.accounts")}
          </Button>
        </DialogActions>
      </Dialog>

      <AccountPeek
        open={!!peekAccountId}
        accountId={peekAccountId}
        onClose={() => setPeekAccountId(null)}
        onChanged={reload}
      />

      <ConfirmationModal title={tNegocio("confirmTitle")} open={!!confirm} onClose={() => setConfirm(null)} onConfirm={remove}>
        {tNegocio("confirmBody")}
      </ConfirmationModal>
      {ticketContact ? (
        <NewTicketModal
          modalOpen
          initialContact={ticketContact}
          onClose={(ticket) => {
            setTicketContact(null);
            if (ticket?.uuid) history.push(`/tickets/${ticket.uuid}`);
          }}
        />
      ) : null}
    </>
  );
}
